
// EditAgencyModal.tsx
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Building2, Camera, Loader2, X } from "lucide-react";
import { useRef, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { Avatar, AvatarImage, AvatarFallback } from "@/app/_module/components/ui/avatar";
import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  DialogTrigger,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/app/_module/components/ui/dialog";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/app/_module/components/ui/form";
import { Input } from "@/app/_module/components/ui/input";
import { Switch } from "@/app/_module/components/ui/switch";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { useToast } from "@/app/_module/hooks/use-toast";
import { getApiUrl } from "@/app/_module/services/config";
import supabaseAuthService from "@/app/_module/services/supabaseAuthService";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";

import type { Agency } from "../../types/types";

interface EditAgencyModalProps {
  defaultValues: Agency;
  onEdit: (data: Agency, close: () => void) => void;
}

const formSchema = z.object({
  id: z.string(),
  name: z.string().min(2, "Name is required"),
  description: z.string().min(2, "Description is required"),
  contactEmail: z.string().email("Invalid email address"),
  location: z.string().optional(),
  isActive: z.boolean().optional(), // Read-only - managed by support
  updatedAt: z.string(),
  createdAt: z.string(),
  deletedAt: z.string().nullable(),
});

const EditAgencyModal = function({ defaultValues, onEdit }: EditAgencyModalProps) {
  const { branding } = useBranding();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...defaultValues,
      // Convert null values to empty strings to avoid React warnings
      description: defaultValues.description ?? '',
      contactEmail: defaultValues.contactEmail ?? '',
      name: defaultValues.name ?? '',
      location: defaultValues.location ?? 'Main Office',
    },
    mode: "onChange",
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { control, handleSubmit, formState: { isValid, errors } } = form;
  const [open, setOpen] = useState(false);

  // Logo upload state
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoImageUrl, setLogoImageUrl] = useState<string | null>(() => {
    // Initialize with agency's logo if available
    const agencyRecord = defaultValues as unknown as Record<string, unknown>;
    return (agencyRecord?.logoUrl as string) || (agencyRecord?.logo_url as string) || null;
  });
  const [imageKey, setImageKey] = useState(0); // Force re-render on image change
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a PNG, JPG, JPEG, GIF, or WebP image.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "Agency logo must be less than 5MB.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploadingLogo(true);

      const formData = new FormData();
      formData.append('file', file);

      // Get Supabase authentication token
      let supabaseToken = await supabaseAuthService.getAccessToken();

      // If no token, try refreshing the session
      if (!supabaseToken) {
        const refreshedSession = await supabaseAuthService.refreshSession();
        if (refreshedSession?.access_token) {
          supabaseToken = refreshedSession.access_token;
        }
      }
      if (!supabaseToken) {
        toast({
          title: "Authentication Error",
          description: "Please refresh the page and try again.",
          variant: "destructive",
        });
        throw new Error('Authentication token not available. Please refresh the page and try again.');
      }

      const apiUrl = getApiUrl(`/agencies/logo/${defaultValues.id}`);

      // Get agency ID for the request header
      const agencyId = AgencyTokenManager.getCurrentAgencyId();

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${supabaseToken}`,
      };

      // Add agency ID header if available
      if (agencyId) {
        headers['x-agency-id'] = agencyId;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to upload agency logo');
      }

      const result = await response.json();
      // Update logo image URL immediately
      const newImageUrl = result.data.logoUrl;
      setLogoImageUrl(newImageUrl);
      setImageKey(prev => prev + 1); // Force re-render of avatar

      // Invalidate all agency-related query caches to update logos everywhere
      queryClient.invalidateQueries({ queryKey: ["agencies"] });
      queryClient.invalidateQueries({ queryKey: ["agency"] });
      queryClient.invalidateQueries({ queryKey: ["agency-status"] });
      queryClient.invalidateQueries({ queryKey: ["user"] }); // For user context with agency data

      toast({
        title: "Agency Logo Updated",
        description: "Your agency logo has been uploaded successfully.",
      });
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: "Failed to upload agency logo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    try {
      setIsUploadingLogo(true);

      let supabaseToken = await supabaseAuthService.getAccessToken();
      if (!supabaseToken) {
        const refreshedSession = await supabaseAuthService.refreshSession();
        if (refreshedSession?.access_token) {
          supabaseToken = refreshedSession.access_token;
        }
      }
      if (!supabaseToken) {
        toast({
          title: "Authentication Error",
          description: "Please refresh the page and try again.",
          variant: "destructive",
        });
        return;
      }

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${supabaseToken}`,
      };
      const agencyId = AgencyTokenManager.getCurrentAgencyId();
      if (agencyId) {
        headers['x-agency-id'] = agencyId;
      }

      const response = await fetch(getApiUrl(`/agencies/logo/${defaultValues.id}`), {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to remove agency logo');
      }

      setLogoImageUrl(null);
      setImageKey(prev => prev + 1);

      // Same cache sweep as upload so the logo disappears everywhere it's shown
      queryClient.invalidateQueries({ queryKey: ["agencies"] });
      queryClient.invalidateQueries({ queryKey: ["agency"] });
      queryClient.invalidateQueries({ queryKey: ["agency-status"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });

      toast({
        title: "Agency Logo Removed",
        description: "Your agency logo has been removed.",
      });
    } catch (error) {
      toast({
        title: "Remove Failed",
        description: "Failed to remove agency logo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    onEdit(data as Agency, () => setOpen(false));
  };

  // Get agency initials for fallback avatar
  const getAgencyInitials = (name: string) => {
    if (!name) return 'AG';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">Edit</Button>
      </DialogTrigger>
      <ResponsiveDialogContent size="2xl">
        <DialogHeader>
          <div className="flex items-center gap-4">
            {/* Agency Logo Upload Section */}
            <div className="relative group cursor-pointer" onClick={handleLogoClick}>
              <Avatar key={`avatar-${imageKey}`} className="h-16 w-16 border border-gray-200">
                <AvatarImage
                  key={`avatar-img-${imageKey}`}
                  src={logoImageUrl || undefined}
                  alt={defaultValues.name || "Agency logo"}
                  className="object-cover"
                />
                <AvatarFallback className="text-lg bg-primary/10 text-primary">
                  <Building2 className="h-8 w-8" />
                </AvatarFallback>
              </Avatar>
              {/* Camera icon badge - bottom right corner */}
              <div className="absolute -bottom-0.5 -right-0.5 bg-primary rounded-full p-1 border border-white shadow-sm group-hover:bg-primary/80 transition-colors">
                {isUploadingLogo ? (
                  <Loader2 className="h-3 w-3 text-white animate-spin" />
                ) : (
                  <Camera className="h-3 w-3 text-white" />
                )}
              </div>
              {/* Remove badge - top right, only when a logo exists (same pattern as
                  the profile picture in Settings). stopPropagation so it doesn't
                  open the file picker. */}
              {logoImageUrl && !isUploadingLogo && (
                <button
                  type="button"
                  aria-label="Remove agency logo"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveLogo();
                  }}
                  className="absolute -top-0.5 -right-0.5 rounded-full bg-red-500 p-1 border border-white shadow-sm opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-opacity"
                >
                  <X className="h-3 w-3 text-white" />
                </button>
              )}
            </div>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
              onChange={handleFileChange}
              className="hidden"
              disabled={isUploadingLogo}
            />
            <div>
              <DialogTitle>Edit Agency</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Click the icon to upload a logo
              </p>
            </div>
          </div>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {/* Hidden ID field to preserve it */}
            <FormField
              control={control}
              name="id"
              render={({ field }) => (
                <input type="hidden" {...field} />
              )}
            />

            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agency Name</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  {!!errors.name && <FormMessage>{errors.name.message}</FormMessage>}
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      className="resize-none"
                      placeholder="Enter agency description"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  {!!errors.description && <FormMessage>{errors.description.message}</FormMessage>}
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="contactEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Email</FormLabel>
                  <FormControl><Input type="email" {...field} /></FormControl>
                  {!!errors.contactEmail && <FormMessage>{errors.contactEmail.message}</FormMessage>}
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Main Office, New York Branch, Remote"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  {!!errors.location && <FormMessage>{errors.location.message}</FormMessage>}
                </FormItem>
              )}
            />
            {/* Active Status - Read-only, contact support to disable */}
            <div className="flex flex-row items-center justify-between rounded-lg border p-4 bg-gray-50">
              <div className="space-y-0.5">
                <div className="text-base font-medium">Active Status</div>
                <div className="text-sm text-muted-foreground">
                  {defaultValues.isActive ? (
                    <>Agency is currently <span className="text-green-600 font-medium">Active</span></>
                  ) : (
                    <>Agency is currently <span className="text-red-600 font-medium">Inactive</span></>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  To disable or reactivate this agency, please contact support at{" "}
                  <a href={`mailto:${branding.supportEmail}`} className="text-primary hover:underline font-medium">
                    {branding.supportEmail}
                  </a>
                </div>
              </div>
              <Switch
                checked={defaultValues.isActive}
                disabled={true}
                className="opacity-50 cursor-not-allowed"
              />
            </div>
            <DialogFooter>
              <Button disabled={!isValid || isUploadingLogo} type="submit">
                {isUploadingLogo ? "Uploading Logo..." : "Save"}
              </Button>
              <DialogClose asChild>
                <Button type="button" variant="outline">Cancel</Button>
              </DialogClose>
            </DialogFooter>
          </form>
        </Form>
      </ResponsiveDialogContent>
    </Dialog>
  );
}

export default EditAgencyModal;
